import React from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { DeleteOutline, EditOutlined, FileDownload } from '@mui/icons-material';
import { useSuspenseQuery } from '@tanstack/react-query';
import throttle from 'lodash/throttle';
import { UploadedProviderDocument, getProviderDocumentDownloadLink, uploadedProviderDocumentTypeLabels } from '@uplift/api';
import { useUserQuery } from '@uplift/queries';
import { Button, Loader, ScrollableView, Tooltip, elementOnScreenPercentage, useBreakpoint } from '@uplift/ui-v2';
import { PDFPage, PDFPagination, usePDFDocument } from '@uplift/pdf-viewer';
import { DocumentLayout } from './components';
import { useDownloadProviderDocument } from './useDownloadProviderDocument';
import { EditUploadedProviderDocumentModal } from '../ProviderDocumentDetailsForm';
import { DeleteUploadedProviderDocumentModal } from '../DeleteUploadedProviderDocumentModal';


export const UploadedProviderDocumentPreview: React.VFC<{
  document: UploadedProviderDocument;
  onDeleteSuccess: () => void;
}> = ({ document, onDeleteSuccess }) => {
  const breakpoint = useBreakpoint();
  const [ editing, setEditing ] = React.useState(false);
  const [ deleting, setDeleting ] = React.useState(false);
  const { data: downloadLink } = useSuspenseQuery({
    queryKey: [ 'providerDocumentDownloadLink', document.id ],
    queryFn: () => getProviderDocumentDownloadLink(document.id),
    select: (res) => res.data.downloadLink,
    staleTime: 5 * 60 * 1000,
  });

  const { data: provider } = useUserQuery(document.providerId, { suspense: true });
  const { data: pdf } = usePDFDocument(downloadLink);
  const { isDownloading, downloadDocument } = useDownloadProviderDocument();
  const [ page, setPage ] = React.useState(0);

  const pages = React.useMemo(() => Array.from({ length: pdf?.numPages ?? 0 }).map((_, index) => index), [ pdf ]);
  const pageElementRefs = React.useRef<HTMLDivElement[]>([]);

  React.useEffect(() => {
    const handleScroll = throttle(() => {
      const items = pageElementRefs.current.map((element, index) => ({
        key: index,
        value: elementOnScreenPercentage(element),
      })).sort((first, second) => second.value - first.value);

      if (items.length) {
        setPage(items[0].key);
      }
    }, 300);

    window.document.addEventListener('scroll', handleScroll, true);
    window.document.dispatchEvent(new Event('scroll'));

    return () => window.document.removeEventListener('scroll', handleScroll, true);
  }, []);

  const label = uploadedProviderDocumentTypeLabels[document.documentType] ?? document.documentType;

  return (
    <Box display="flex" flexDirection="row" flex={1}>
      <DocumentLayout
        title={label}
        subtitle={provider ? `uploaded by ${provider.firstName[0]}. ${provider.lastName}` : undefined}
        contentSpacing={false}
        controls={(
          <Box>
            <Divider />
            <Box boxSizing="border-box" p={2} display="flex" justifyContent="space-between" alignItems="center" bgcolor="white">
              <Box flex={1}>
                <Button
                  onClick={() => setDeleting(true)}
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutline />}
                >Delete</Button>
              </Box>
              <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                {pdf && <Typography variant="body1">Page {page + 1}/{pdf.numPages}</Typography>}
              </Box>
              <Box flex={1} display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={isDownloading}
                  startIcon={<FileDownload />}
                  onClick={() => downloadDocument(downloadLink, `${label.replace(/\s+/g, '_')}.pdf`)}
                >
                  {isDownloading ? 'Downloading…' : 'Download'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      >
        <Box p={2} pr={0} bgcolor="grey.50" display="flex" alignItems="center" justifyContent="space-between">
          <Typography>Description: {document.description}</Typography>
          <Tooltip title="Edit document" arrow>
            <IconButton color="secondary" onClick={() => setEditing(true)}>
              <EditOutlined />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <Box width="100%" display="flex" flexDirection="column" gap="16px" pt={2}>
          {pdf && pages.map(pageNumber => (
            <React.Suspense key={pageNumber} fallback={<Loader color="secondary" height="500px" />}>
              <div
                ref={element => {
                  if (element) {
                    pageElementRefs.current[pageNumber] = element;
                  }
                }}
              >
                <PDFPage pdf={pdf} pageNumber={pageNumber} zoomAndDrag={true} />
              </div>
            </React.Suspense>
          ))}
        </Box>
        {editing && (
          <EditUploadedProviderDocumentModal
            open={editing}
            onClose={() => setEditing(false)}
            onSuccess={async () => {
              setEditing(false);
            }}
            document={document}
          />
        )}
        {deleting && (
          <DeleteUploadedProviderDocumentModal
            open={deleting}
            onClose={() => setDeleting(false)}
            onSuccess={async () => {
              setDeleting(false);
              onDeleteSuccess();
            }}
            document={document}
          />
        )}
      </DocumentLayout>
      {pdf && pdf.numPages > 1 && breakpoint.md && (
        <ScrollableView height="100%" maxWidth="150px" minWidth="112px">
          <Box display="flex" flex="1" pb={8} pt={3} justifyContent="center">
            <Box maxWidth="70%">
              <PDFPagination
                pdf={pdf}
                pageNumber={page}
                setPage={(pageNumber) => {
                  pageElementRefs.current[pageNumber]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
                }}
              />
            </Box>
          </Box>
        </ScrollableView>
      )}
    </Box>
  );
};
